import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import AddCircleIcon from "@/assets/icons/misc/add-circle-icon.tsx"
import CloseCrossIcon from "@/assets/icons/misc/close-cross-icon.tsx"
import RemoveCircleIcon from "@/assets/icons/misc/remove-circle-icon.tsx"
import CircleTickIcon from "@/assets/icons/task/circle-tick-icon.tsx"
import BirdIcon from "@/assets/svg/bird-icon.svg?react"
import { type RenderFunctionDrawerItemComponentProps } from "@/components/render-function-drawer/render-function-drawer.tsx"
import { useGetBirdsGroupByIdQuery } from "@/features/birds-groups/api/birds-groups-api.ts"
import { useGetBirdSpecies } from "@/features/birds-groups/hooks/use-get-bird-species.ts"
import { BIRDS_GROUP_LIST_VIEW } from "@/features/birds-groups/routes/route-list.ts"
import { type BirdsGroups } from "@/features/birds-groups/types/birds-groups.ts"
import styles from "@/features/tasks/components/associated-list/associated-list.module.css"
import { cn } from "@/utils/styles.ts"

export const AssociatedBirdGroupCard: React.FC<
  RenderFunctionDrawerItemComponentProps
> = ({ id, onDelete, isSelected, onSelect }) => {
  const { data: birdGroup } = useGetBirdsGroupByIdQuery(id)
  const birdSpecies = useGetBirdSpecies()
  const selectedSpecies = birdSpecies?.find(
    (species) => species.id === birdGroup?.species,
  )
  const { t } = useTranslation()
  const navigate = useNavigate()

  const navigateToBirdGroup = () => {
    navigate(`${BIRDS_GROUP_LIST_VIEW}/${id}`)
  }

  const handleClick = () => {
    onSelect && onSelect()
    !onSelect && navigateToBirdGroup()
  }

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation()
    onDelete && onDelete(id)
  }

  return (
    <div
      onClick={handleClick}
      className={cn(
        "flex items-center justify-between gap-2 rounded-[10px] border border-gray-200 bg-[var(--light-grey)] p-2 text-neutral-700",
        isSelected && "border border-brown-500 bg-white",
      )}
    >
      <div
        className={cn(
          "grid w-[70%] grid-cols-[30px_auto] items-center gap-2",
          onSelect && "w-full grid-cols-[30px_30px_auto]",
        )}
      >
        {onSelect && (
          <>
            {isSelected ? (
              <CircleTickIcon />
            ) : (
              <div
                className={
                  "border-dark-grey box-border h-[24px] w-[24px] rounded-full border bg-[var(--background-color)] pr-3"
                }
              />
            )}
          </>
        )}
        <div
          className={cn(
            "grid h-[30px] w-[30px] place-content-center rounded-[4px] bg-gray-600 text-white opacity-60",
            isSelected && "bg-brown-500",
          )}
        >
          <BirdIcon
            className={cn(
              "h-[30px] w-[30px] rounded bg-gray-600 p-1 text-white",
              isSelected && "bg-brown-500",
            )}
          />
        </div>
        <div className={"flex w-[70%] flex-col"}>
          <div className={"truncate text-[14px]"}>{birdGroup?.name}</div>
          <div className={"truncate text-neutral-700"}>
            {t("species")} {selectedSpecies && t(selectedSpecies?.name)}
          </div>
        </div>
      </div>
      {onDelete && (
        <div onClick={handleDelete}>
          <CloseCrossIcon className={"text-neutral-500"} />
        </div>
      )}
    </div>
  )
}

interface AssociatedBirdGroupListProps {
  birdsGroups: BirdsGroups[]
  selectedBirdsGroupsId: string[]
  onDelete: (birdGroupId: string[]) => void
}

export const AssociatedBirdGroupList: React.FC<
  AssociatedBirdGroupListProps
> = ({ birdsGroups, selectedBirdsGroupsId, onDelete }) => {
  const { t } = useTranslation()
  const selectedQueens = birdsGroups.filter((queen) =>
    selectedBirdsGroupsId.includes(queen.id),
  )
  const [listLimit, setListLimit] = React.useState(3)
  const expandList = () => setListLimit(selectedQueens.length)
  const collapseList = () => setListLimit(3)
  const list = selectedQueens.slice(0, listLimit)

  const handleDelete = (queenId: string) => {
    const newQueens = selectedQueens.filter((queen) => queen.id !== queenId)
    onDelete(newQueens.map((q) => q.id))
  }

  return (
    <div className={"flex flex-col gap-1"}>
      {list.map((queen, index) => (
        <AssociatedBirdGroupCard
          id={queen.id}
          key={index}
          onDelete={handleDelete}
        />
      ))}
      {selectedQueens.length > listLimit && (
        <i className={styles["more-items"]} onClick={expandList}>
          <AddCircleIcon size={20} /> {t("seeOtherHivesAssociated")}
        </i>
      )}
      {selectedQueens.length === listLimit &&
        selectedBirdsGroupsId.length > 3 && (
          <i className={styles["more-items"]} onClick={collapseList}>
            <RemoveCircleIcon size={20} /> {t("hideOtherHivesAssociated")}
          </i>
        )}
    </div>
  )
}
